import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Select,
  Textarea,
  Alert,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { useCreateRequest } from '../../hooks/useRequests';

const REQUEST_TYPE_OPTIONS = [
  { value: 'time_off', label: 'Time Off' },
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick_leave', label: 'Sick Leave' },
  { value: 'personal', label: 'Personal Leave' },
  { value: 'bereavement', label: 'Bereavement' },
  { value: 'jury_duty', label: 'Jury Duty' },
  { value: 'schedule_change', label: 'Schedule Change' },
  { value: 'other', label: 'Other' },
];

export default function RequestCreatePage() {
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      requestType: '',
      dateStart: null as Date | null,
      dateEnd: null as Date | null,
      message: '',
    },
    validate: {
      requestType: (v: string) => (!v ? 'Please select a request type' : null),
      dateStart: (v: Date | null) => (!v ? 'Start date is required' : null),
      dateEnd: (v: Date | null, values: { dateStart: Date | null }) => {
        if (!v) return 'End date is required';
        if (values.dateStart && v < values.dateStart) return 'End date must be after start date';
        return null;
      },
      message: (v: string) => (v.length < 1 ? 'Please provide a reason' : null),
    },
  });

  const createRequestHook = useCreateRequest();
  const createLoading = createRequestHook.isPending;

  const handleSubmit = form.onSubmit((values) => {
    setSuccess(false);
    setError(null);
    createRequestHook.mutate(
      {
        input: {
          requestType: values.requestType,
          dateStart: values.dateStart?.toISOString().split('T')[0],
          dateEnd: values.dateEnd?.toISOString().split('T')[0],
          message: values.message,
        },
      },
      {
        onSuccess: () => {
          setSuccess(true);
          setError(null);
          form.reset();
          setTimeout(() => navigate('/admin/workforce/leave/requests'), 2000);
        },
        onError: (err: any) => {
          setError(err.message || 'Failed to submit request');
          setSuccess(false);
        },
      },
    );
  });

  return (
    <Stack gap="md">
      <PageHeader
        title="New Request"
        subtitle="Submit a new time-off or schedule request"
        breadcrumbs={[
          { label: 'Requests', to: '/admin/workforce/leave/requests' },
          { label: 'New Request' },
        ]}
      />

      {success && (
        <Alert icon={<IconCheck size={16} />} color="green" title="Submitted">
          Your request has been submitted successfully. Redirecting...
        </Alert>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {error}
        </Alert>
      )}

      <Paper withBorder p="lg" radius="md">
        <form onSubmit={handleSubmit}>
          <Stack gap="md" maw={600}>
            <Select
              label="Request Type"
              placeholder="Select type of request"
              required
              data={REQUEST_TYPE_OPTIONS}
              {...form.getInputProps('requestType')}
            />

            <Group grow>
              <DateInput
                label="Start Date"
                placeholder="Select start date"
                required
                clearable
                {...form.getInputProps('dateStart')}
              />
              <DateInput
                label="End Date"
                placeholder="Select end date"
                required
                clearable
                minDate={form.values.dateStart || undefined}
                {...form.getInputProps('dateEnd')}
              />
            </Group>

            <Textarea
              label="Reason / Notes"
              placeholder="Provide details about your request..."
              required
              minRows={3}
              {...form.getInputProps('message')}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => navigate('/admin/workforce/leave/requests')}>
                Cancel
              </Button>
              <Button type="submit" loading={createLoading}>
                Submit Request
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}
